// src/modules/hr/pages/HolidayCalendar.jsx — Phase 16 + 17
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Typography, Row, Col, Button, Select, Tag, Tooltip, Modal, Form,
  DatePicker, Input, message, Space, Popconfirm, Alert, Switch,
} from 'antd';
import { CalendarOutlined, ReloadOutlined, PlusOutlined, ThunderboltOutlined, BankOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { usePermissions } from '../../../hooks/usePermissions';
import { fetchBranches } from '../../branches/api';
import { fetchHolidays, createHoliday, cancelHoliday, fetchClosurePreview, applyHolidayClosure } from '../api';
import { HOLIDAY_TYPE_OPTIONS, tagColor, tagLabel } from '../constants/hrConstants';

const { Title, Text } = Typography;

// Indian states (subset commonly used; extend as needed)
const STATES = ['West Bengal', 'Delhi', 'Maharashtra', 'Karnataka', 'Tamil Nadu', 'Bihar', 'Uttar Pradesh', 'Gujarat', 'Rajasthan', 'Punjab', 'Kerala', 'Telangana', 'Odisha', 'Assam', 'Jharkhand'];

export default function HolidayCalendar() {
  const { can } = usePermissions();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [typeFilter, setTypeFilter] = useState(null);
  const [branches, setBranches] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => { fetchBranches({ limit: 1000 }).then((r) => setBranches(r?.data?.data || r?.data || [])).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year };
      if (typeFilter) params.type = typeFilter;
      const res = await fetchHolidays(params);
      setData(res?.data?.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load holidays'); }
    finally { setLoading(false); }
  }, [year, typeFilter]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const payload = {
        ...v,
        date: v.date?.toISOString(),
        endDate: v.endDate?.toISOString(),
        isEmergency: v.type === 'EMERGENCY',
      };
      const res = await createHoliday(payload);
      const closure = res?.data?.treasuryClosure;
      message.success('Holiday created' + (closure ? ` · ${closure.closed} branch-day(s) auto-closed` : ''));
      setAddOpen(false); form.resetFields(); load();
    } catch (err) { if (!err?.errorFields) message.error(err?.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const doApplyClosure = async (row) => {
    try {
      const res = await applyHolidayClosure(row._id);
      const r = res?.data?.data;
      message.success(`Auto-closure: ${r.closed} closed, ${r.skipped} skipped, ${r.errored} errored`);
      load();
    } catch (err) { message.error(err?.response?.data?.message || 'Closure failed'); }
  };

  const doPreview = async (row) => {
    try {
      const res = await fetchClosurePreview(row._id);
      setPreview({ holiday: row, ...(res?.data?.data || {}) });
    } catch (err) { message.error(err?.response?.data?.message || 'Preview failed'); }
  };

  const columns = [
    { title: 'Date', dataIndex: 'date', width: 120, render: (v, r) => dayjs(v).format('DD MMM YYYY') + (r.endDate ? ` → ${dayjs(r.endDate).format('DD MMM')}` : '') },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Type', dataIndex: 'type', width: 110, render: (v) => <Tag color={tagColor(HOLIDAY_TYPE_OPTIONS, v)}>{tagLabel(HOLIDAY_TYPE_OPTIONS, v)}</Tag> },
    { title: 'Applies To', key: 'applies', render: (_, r) => r.type === 'NATIONAL' ? 'All branches' : r.type === 'STATE' ? (r.states || []).join(', ') : (r.branches || []).map((b) => b.name || b).join(', ') || '—' },
    { title: 'Treasury', dataIndex: 'treasuryClosureDone', width: 110, render: (v) => v ? <Tag color="purple">Auto-Closed</Tag> : <Tag>Pending</Tag> },
    {
      title: 'Actions', key: 'a', width: 220, fixed: 'right',
      render: (_, r) => r.status === 'CANCELLED' ? <Tag>Cancelled</Tag> : (
        <Space>
          <Tooltip title="Preview affected branches"><Button size="small" icon={<BankOutlined />} onClick={() => doPreview(r)} /></Tooltip>
          {can('hrHoliday', 'edit') && (
            <Popconfirm title="Run treasury auto-closure for this holiday?" okText="Run" onConfirm={() => doApplyClosure(r)}>
              <Button size="small" type="primary" ghost icon={<ThunderboltOutlined />}>Auto-Close</Button>
            </Popconfirm>
          )}
          {can('hrHoliday', 'delete') && <Popconfirm title="Cancel this holiday?" onConfirm={() => cancelHoliday(r._id).then(() => { message.success('Cancelled'); load(); })}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>}
        </Space>
      ),
    },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><CalendarOutlined style={{ marginRight: 8 }} />Holiday Calendar</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>National · State · Branch · Emergency — drives treasury auto-closure</Text>
        </Col>
        <Col>
          <Space>
            <Select value={year} onChange={setYear} style={{ width: 100 }}>{years.map((y) => <Select.Option key={y} value={y}>{y}</Select.Option>)}</Select>
            <Select allowClear placeholder="Type" style={{ width: 130 }} options={HOLIDAY_TYPE_OPTIONS} value={typeFilter} onChange={(v) => setTypeFilter(v || null)} />
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={load} /></Tooltip>
            {can('hrHoliday', 'create') && <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Add Holiday</Button>}
          </Space>
        </Col>
      </Row>

      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="Holiday → Treasury auto-closure"
        description="When applied, each affected branch's business day is closed automatically: previous approved closing cash becomes the holiday opening & closing cash, the denomination snapshot is copied, difference is ₹0, and status is HOLIDAY_AUTO_CLOSED. Emergency holidays auto-close on creation." />

      <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" scroll={{ x: 1100 }} pagination={{ pageSize: 50 }} />

      <Modal title="Add Holiday" open={addOpen} onCancel={() => setAddOpen(false)} onOk={submit} okButtonProps={{ loading: saving }} width={620} destroyOnClose>
        <Form form={form} layout="vertical" initialValues={{ type: 'NATIONAL', autoCloseTreasury: true }}>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="name" label="Name" rules={[{ required: true }]}><Input placeholder="e.g. Republic Day" /></Form.Item></Col>
            <Col span={12}><Form.Item name="type" label="Type" rules={[{ required: true }]}><Select options={HOLIDAY_TYPE_OPTIONS} /></Form.Item></Col>
            <Col span={12}><Form.Item name="date" label="Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="endDate" label="End Date (multi-day)"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type}>
            {({ getFieldValue }) => {
              const t = getFieldValue('type');
              if (t === 'STATE') return <Form.Item name="states" label="States" rules={[{ required: true }]}><Select mode="multiple" options={STATES.map((s) => ({ label: s, value: s }))} /></Form.Item>;
              if (t === 'BRANCH' || t === 'EMERGENCY') return <Form.Item name="branches" label="Branches" rules={t === 'BRANCH' ? [{ required: true }] : []}><Select mode="multiple" showSearch optionFilterProp="children" placeholder={t === 'EMERGENCY' ? 'Leave blank for all branches' : 'Select branches'}>{branches.map((b) => <Select.Option key={b._id} value={b._id}>{b.name}</Select.Option>)}</Select></Form.Item>;
              return null;
            }}
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.type !== c.type}>
            {({ getFieldValue }) => getFieldValue('type') === 'EMERGENCY' ? <Form.Item name="reason" label="Reason"><Input placeholder="Flood / Shutdown / Power Failure …" /></Form.Item> : null}
          </Form.Item>
          <Form.Item name="autoCloseTreasury" label="Auto-close treasury" valuePropName="checked"><Switch checkedChildren="Yes" unCheckedChildren="No" /></Form.Item>
        </Form>
      </Modal>

      <Modal title="Closure Preview" open={!!preview} onCancel={() => setPreview(null)} footer={<Button onClick={() => setPreview(null)}>Close</Button>} width={560}>
        {preview && (
          <>
            <Text>{preview.holiday?.name} — <b>{preview.totalDayClosures}</b> branch-day closure(s) across <b>{preview.branches?.length || 0}</b> branch(es) × <b>{preview.dates?.length || 0}</b> day(s).</Text>
            <Table style={{ marginTop: 12 }} size="small" rowKey="_id" pagination={false}
              dataSource={preview.branches || []}
              columns={[{ title: 'Branch', dataIndex: 'name' }, { title: 'Code', dataIndex: 'code' }, { title: 'State', dataIndex: ['address', 'state'], render: (v, r) => r.address?.state || '—' }]}
              locale={{ emptyText: 'No applicable branches' }} />
          </>
        )}
      </Modal>
    </div>
  );
}
